import { ChecklistStatusValue } from "../../../../constants";
// import IntegrationChecklistAccordionTitleV2 from "./IntegrationChecklistAccordionTitleV2";
import { AlertTriangle, CircleCheck } from "lucide-react";
import AccordionCard from "../../../integration-builder/shared/AccordionCard";
import { usePublishModuleContext } from "../context/PublishModuleContext";
import IntegrationChecklistResultsContainerV2 from "../IntegrationChecklistResults/IntegrationChecklistResultsContainerV2";
import PublishModuleTestExecutionsContainerV2 from "../MappingTestResults/MappingTestResultsV2";
import MappingTestAccordionTitleV2 from "./MappingTestAccordionTitleV2";
import AutomatedTestAccordionTitleV2 from "../automated-tests/components/AutomatedTestAccordionTitleV2";
import AutomatedTestResultsContainer from "../automated-tests/components/AutomatedTestResultsContainer";
import { Badge, Text, Alert, Accordion } from "@merge-api/merge-javascript-shared";
import useCheckAutomatedTestRegistry from "../automated-tests/hooks/useCheckAutomatedTestRegistry";
import usePollAutomatedTestSuiteExecution from "../automated-tests/hooks/usePollAutomatedTestSuiteExecution";
import { TestExecutionStatus } from "../automated-tests/types";
import { useEffect, useCallback, useRef } from "react";
import { useMergeFlag, MergeFlagFeature } from "../../../shared/hooks/useMergeFlag";

const PublishModuleChecksCardV2 = () => {
  const {
    integrationChecklist,
    integrationID,
    setAutomatedTestIntegrationVersionId,
    setIsRunningAutomatedTests,
    setTriggerAutomatedTestPoll,
    setUpdateAutomatedTestExecution,
    setAreAutomatedTestsFailing,
  } = usePublishModuleContext();

  const { enabled: isE2ETestSuitesFlagEnabled } = useMergeFlag({
    feature: MergeFlagFeature.MERGE_FLAG_E2E_TEST_SUITES,
    isEnabledForUser: true,
  });

  const {
    hasTests,
    loading: isLoadingTestRegistry,
    integrationVersionId,
  } = useCheckAutomatedTestRegistry({
    integrationID,
  });

  // Store integration version ID in context when it's available
  useEffect(() => {
    if (integrationVersionId) {
      setAutomatedTestIntegrationVersionId(integrationVersionId);
    }
  }, [integrationVersionId, setAutomatedTestIntegrationVersionId]);

  // Callback to update context when tests progress changes
  const handleTestsInProgressChange = useCallback(
    (inProgress: boolean) => {
      setIsRunningAutomatedTests(inProgress);
    },
    [setIsRunningAutomatedTests]
  );

  // Poll for automated test suite execution results
  const {
    automatedTestSuiteExecution,
    fetchData,
    setAutomatedTestSuiteExecution,
  } = usePollAutomatedTestSuiteExecution({
    integrationID,
    integrationVersionId,
    hasTests,
    onTestsInProgressChange: handleTestsInProgressChange,
  });

  // Use a ref to store the fetchData function and update context only when mounted
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  useEffect(() => {
    // Provide a stable function reference to context
    setTriggerAutomatedTestPoll(() => () => fetchDataRef.current());
  }, [setTriggerAutomatedTestPoll]);

  // Provide setAutomatedTestSuiteExecution to context for immediate updates
  useEffect(() => {
    setUpdateAutomatedTestExecution(() => setAutomatedTestSuiteExecution);
  }, [setAutomatedTestSuiteExecution, setUpdateAutomatedTestExecution]);

  // Set blocking state if hasTests is true and tests haven't run or have failed
  useEffect(() => {
    // If flag is not enabled, don't block
    if (!isE2ETestSuitesFlagEnabled) {
      setAreAutomatedTestsFailing(false);
      return;
    }

    if (!hasTests) {
      // If no tests exist, don't block
      setAreAutomatedTestsFailing(false);
      return;
    }

    if (
      !automatedTestSuiteExecution ||
      Object.keys(automatedTestSuiteExecution.tests).length === 0
    ) {
      // If tests exist but haven't been run, block publish
      setAreAutomatedTestsFailing(true);
      return;
    }

    // Check if any test has failed, aborted, is pending, or in progress
    const testStatuses = Object.values(automatedTestSuiteExecution.tests).map(
      (test) => test.status
    );
    const hasFailedOrIncompleteTests = testStatuses.some(
      (status) =>
        status === TestExecutionStatus.FAILED ||
        status === TestExecutionStatus.ABORTED ||
        status === TestExecutionStatus.PENDING ||
        status === TestExecutionStatus.IN_PROGRESS
    );

    setAreAutomatedTestsFailing(hasFailedOrIncompleteTests);
  }, [
    hasTests,
    automatedTestSuiteExecution,
    setAreAutomatedTestsFailing,
    isE2ETestSuitesFlagEnabled,
  ]);

  const failureCount = integrationChecklist
    ? Object.values(integrationChecklist).filter(
        (value) => value == ChecklistStatusValue.NOT_IMPLEMENTED
      ).length
    : 0;

  return (
    <div className="space-y-4">
      <AccordionCard
        chevronSize={16}
        chevronOrientation="down"
        onChange={function noRefCheck() {}}
        title={
          <div className="flex flex-row items-center space-x-2">
            {failureCount > 0 ? (
              <AlertTriangle className="text-yellow-60" size={16} />
            ) : (
              <CircleCheck size={16} className="text-teal-70" />
            )}
            {/* The mapping tests accordion title has a button in it adding 4px of height to the main title content */}
            {/* We need to add 2px of padding to the text to make it look the same */}
            <Text variant="h5" className="text-black py-[2px] pl-1">
              Integration Checklist
            </Text>
            {failureCount > 0 && (
              <Badge color="yellow">
                {failureCount.toString() + (failureCount > 1 ? " warnings" : " warning")}
              </Badge>
            )}
          </div>
        }
        titleClassName="p-5"
        defaultExpanded={false}
        variant="shadow"
        className="mb-0"
      >
        <IntegrationChecklistResultsContainerV2 />
      </AccordionCard>
      {isE2ETestSuitesFlagEnabled && (
        <AccordionCard
          chevronSize={16}
          chevronOrientation="down"
          onChange={function noRefCheck() {}}
          title={
            <AutomatedTestAccordionTitleV2
              automatedTestSuiteExecution={automatedTestSuiteExecution}
            />
          }
          titleClassName="p-5"
          defaultExpanded={false}
          variant="shadow"
          className="mb-0"
          disabled={!hasTests && !isLoadingTestRegistry}
        >
          <AutomatedTestResultsContainer
            automatedTestSuiteExecution={automatedTestSuiteExecution}
          />
        </AccordionCard>
      )}
      <Accordion
        chevronOrientation="down"
        chevronPlacement="right"
        chevronSize={12}
        onChange={function noRefCheck() {}}
        title={
          <Text as="div" className="mr-1 text-[10px] text-gray-50" variant="sm">
            Deprecated checks
          </Text>
        }
        titleClassName="pl-0"
        variant="none"
      >
        <div className="pr-5 pb-5 pl-0 mt-2">
          <Alert color="gray">
            <Text variant="sm">
              Mapping tests have been deprecated. All tests must now be written in the automated
              test repository.{" "}
              <a
                href="https://www.notion.so/mergeapi/E2E-Testing-Enablement-guide-24c1da993e97806db54bf1fe5d692cbf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-60 hover:underline"
              >
                View the E2E Testing Enablement guide
              </a>{" "}
              for more details.
            </Text>
          </Alert>
          <AccordionCard
            className="mt-2"
            chevronSize={16}
            chevronOrientation="down"
            onChange={function noRefCheck() {}}
            defaultExpanded={false}
            title={<MappingTestAccordionTitleV2 />}
            titleClassName="p-5"
            variant="shadow"
          >
            <PublishModuleTestExecutionsContainerV2 />
          </AccordionCard>
        </div>
      </Accordion>
    </div>
  );
};

export default PublishModuleChecksCardV2;
